import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ChevronDown,
  Landmark,
  Mail,
  Phone,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Wallet,
  IdCard,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const Register = () => {
  const [staffId, setStaffId] = useState('');
  const [fullName, setFullName] = useState('');
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [bvnOrNin, setBvnOrNin] = useState('');
  const [department, setDepartment] = useState('Operations');
  const [contribution, setContribution] = useState('10000');
  const [agree, setAgree] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 980);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const contributionLabel = useMemo(() => {
    const value = Number(contribution || 0);
    return `₦${value.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
  }, [contribution]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!staffId.trim() || !fullName.trim() || !emailOrPhone.trim() || !bvnOrNin.trim()) return;
    if (!agree) return;

    login('member', { username: fullName || staffId, password: 'demo-password' });
    navigate('/member');
  };

  return (
    <div style={styles.page}>
      <div style={styles.bgGlowOne} />
      <div style={styles.bgGlowTwo} />

      <div
        style={{
          ...styles.layout,
          ...(isMobile ? styles.layoutMobile : {}),
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: isMobile ? 0 : -28, y: isMobile ? 18 : 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.45 }}
          style={{
            ...styles.leftPanel,
            ...(isMobile ? styles.leftPanelMobile : {}),
          }}
        >
          <div style={styles.brandRow}>
            <div style={styles.logoBadge}>
              <Landmark size={22} />
            </div>
            <div>
              <div style={styles.brandTitle}>FAAN Cooperative</div>
              <div style={styles.brandSub}>Member Enrollment & Onboarding</div>
            </div>
          </div>

          <div style={styles.heroBadge}>
            <Sparkles size={14} />
            Start your cooperative membership journey
          </div>

          <h1
            style={{
              ...styles.heroTitle,
              ...(isMobile ? styles.heroTitleMobile : {}),
            }}
          >
            Create your FAAN cooperative account and unlock savings, loans, dividends, and investments.
          </h1>

          <p style={styles.heroText}>
            Complete your onboarding with staff identity, contact details, and
            contribution preference. In the full system, records can be verified
            against HR, BVN, or NIN before admin approval.
          </p>

          <div style={styles.featureGrid}>
            <div style={styles.featureCard}>
              <div style={styles.featureIconWrap}>
                <ShieldCheck size={18} />
              </div>
              <div>
                <div style={styles.featureTitle}>Verified Registration</div>
                <div style={styles.featureText}>
                  Staff ID and BVN/NIN can be validated before activation.
                </div>
              </div>
            </div>

            <div style={styles.featureCard}>
              <div style={styles.featureIconWrap}>
                <Wallet size={18} />
              </div>
              <div>
                <div style={styles.featureTitle}>Contribution Setup</div>
                <div style={styles.featureText}>
                  Choose your preferred monthly contribution during onboarding.
                </div>
              </div>
            </div>

            <div style={styles.featureCard}>
              <div style={styles.featureIconWrap}>
                <BadgeCheck size={18} />
              </div>
              <div>
                <div style={styles.featureTitle}>Admin Activation</div>
                <div style={styles.featureText}>
                  Cooperative admin can review and activate your member profile.
                </div>
              </div>
            </div>
          </div>

          <div style={styles.bottomInfoCard}>
            <div style={styles.bottomInfoTitle}>Membership flow</div>
            <div style={styles.bottomInfoText}>
              Register → identity validation → admin approval → wallet activation →
              contribution and cooperative services access.
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: isMobile ? 0 : 28, y: isMobile ? 18 : 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05 }}
          style={styles.rightPanel}
        >
          <div
            style={{
              ...styles.formShell,
              ...(isMobile ? styles.formShellMobile : {}),
            }}
          >
            <div style={styles.formTop}>
              <div style={styles.formTitle}>Create member account</div>
              <div style={styles.formSub}>
                Fill in your information to begin cooperative onboarding.
              </div>
            </div>

            <div style={styles.progressCard}>
              <div style={styles.progressHeader}>
                <div style={styles.progressTitle}>Onboarding status</div>
                <div style={styles.progressValue}>Step 1 of 3</div>
              </div>
              <div style={styles.progressTrack}>
                <div style={styles.progressFill} />
              </div>
              <div style={styles.progressText}>
                Registration details will be submitted for review and profile activation.
              </div>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div
                style={{
                  ...styles.twoCol,
                  ...(isMobile ? styles.twoColMobile : {}),
                }}
              >
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Staff ID</label>
                  <div style={styles.inputWrap}>
                    <IdCard size={18} color="#64748b" />
                    <input
                      type="text"
                      value={staffId}
                      onChange={(e) => setStaffId(e.target.value)}
                      placeholder="Enter staff ID"
                      style={styles.input}
                      required
                    />
                  </div>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Full Name</label>
                  <div style={styles.inputWrap}>
                    <UserPlus size={18} color="#64748b" />
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Enter full name"
                      style={styles.input}
                      required
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  ...styles.twoCol,
                  ...(isMobile ? styles.twoColMobile : {}),
                }}
              >
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Official Email / Phone</label>
                  <div style={styles.inputWrap}>
                    {emailOrPhone.includes('@') ? (
                      <Mail size={18} color="#64748b" />
                    ) : (
                      <Phone size={18} color="#64748b" />
                    )}
                    <input
                      type="text"
                      value={emailOrPhone}
                      onChange={(e) => setEmailOrPhone(e.target.value)}
                      placeholder="Enter official email or phone"
                      style={styles.input}
                      required
                    />
                  </div>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>BVN / NIN</label>
                  <div style={styles.inputWrap}>
                    <ShieldCheck size={18} color="#64748b" />
                    <input
                      type="text"
                      value={bvnOrNin}
                      onChange={(e) => setBvnOrNin(e.target.value)}
                      placeholder="Enter BVN or NIN"
                      style={styles.input}
                      required
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  ...styles.twoCol,
                  ...(isMobile ? styles.twoColMobile : {}),
                }}
              >
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Department</label>
                  <div style={styles.selectWrap}>
                    <Building2 size={18} color="#64748b" />
                    <select
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      style={styles.select}
                    >
                      <option>Operations</option>
                      <option>Engineering</option>
                      <option>Finance</option>
                      <option>Security</option>
                      <option>Administration</option>
                      <option>Commercial</option>
                    </select>
                    <ChevronDown size={16} color="#64748b" />
                  </div>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Monthly Contribution</label>
                  <div style={styles.inputWrap}>
                    <Wallet size={18} color="#64748b" />
                    <input
                      type="number"
                      value={contribution}
                      onChange={(e) => setContribution(e.target.value)}
                      placeholder="Enter amount"
                      style={styles.input}
                      min="100"
                      step="100"
                    />
                  </div>
                </div>
              </div>

              <div style={styles.summaryCard}>
                <div style={styles.summaryHeader}>
                  <CheckCircle2 size={18} />
                  Contribution Preference Summary
                </div>
                <div style={styles.summaryBody}>
                  <div style={styles.summaryRow}>
                    <span>Department</span>
                    <strong>{department}</strong>
                  </div>
                  <div style={styles.summaryRow}>
                    <span>Monthly Contribution</span>
                    <strong>{contributionLabel}</strong>
                  </div>
                </div>
              </div>

              <label style={styles.agreeWrap}>
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  style={styles.checkbox}
                />
                <span>
                  I confirm that my registration details are correct and may be
                  reviewed for cooperative onboarding.
                </span>
              </label>

              <button type="submit" style={styles.submitButton}>
                Submit Registration
                <ArrowRight size={17} />
              </button>
            </form>

            <div style={styles.demoHint}>
              Demo note: submitting this form logs you in directly as a member for preview purposes.
            </div>

            <div style={styles.footerText}>
              Already have an account?{' '}
              <Link to="/" style={styles.footerLink}>
                Return to login
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background:
      'radial-gradient(circle at top left, rgba(34,197,94,0.14), transparent 24%), linear-gradient(180deg, #f7fff9 0%, #f8fafc 100%)',
    position: 'relative',
    overflow: 'hidden',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px 18px',
  },
  bgGlowOne: {
    position: 'absolute',
    top: -120,
    right: -60,
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: 'rgba(34,197,94,0.18)',
    filter: 'blur(80px)',
    pointerEvents: 'none',
  },
  bgGlowTwo: {
    position: 'absolute',
    bottom: -120,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: 'rgba(16,185,129,0.12)',
    filter: 'blur(90px)',
    pointerEvents: 'none',
  },
  layout: {
    width: '100%',
    maxWidth: 1320,
    display: 'grid',
    gridTemplateColumns: '1.08fr 0.92fr',
    gap: 22,
    position: 'relative',
    zIndex: 2,
    alignItems: 'stretch',
  },
  layoutMobile: {
    gridTemplateColumns: '1fr',
    gap: 18,
    maxWidth: 720,
  },
  leftPanel: {
    padding: 28,
    borderRadius: 30,
    background: 'linear-gradient(135deg, rgba(5,150,105,0.96), rgba(15,23,42,0.95))',
    color: '#fff',
    boxShadow: '0 30px 70px rgba(5,150,105,0.22)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 780,
  },
  leftPanelMobile: {
    minHeight: 'unset',
    padding: 22,
    borderRadius: 24,
  },
  rightPanel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  formShell: {
    width: '100%',
    maxWidth: 560,
    padding: 28,
    borderRadius: 30,
    background: 'rgba(255,255,255,0.88)',
    border: '1px solid rgba(15,23,42,0.06)',
    boxShadow: '0 24px 60px rgba(15,23,42,0.08)',
    backdropFilter: 'blur(16px)',
  },
  formShellMobile: {
    maxWidth: '100%',
    padding: 22,
    borderRadius: 24,
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  logoBadge: {
    width: 48,
    height: 48,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.14)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandTitle: {
    fontSize: 18,
    fontWeight: 800,
  },
  brandSub: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.74)',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    width: 'fit-content',
    padding: '8px 12px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.12)',
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 18,
  },
  heroTitle: {
    margin: 0,
    fontSize: 36,
    lineHeight: 1.15,
    fontWeight: 900,
    maxWidth: 760,
  },
  heroTitleMobile: {
    fontSize: 29,
    maxWidth: '100%',
  },
  heroText: {
    fontSize: 15,
    lineHeight: 1.75,
    color: 'rgba(255,255,255,0.82)',
    marginTop: 16,
    marginBottom: 24,
    maxWidth: 680,
  },
  featureGrid: {
    display: 'grid',
    gap: 14,
    marginBottom: 22,
  },
  featureCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 20,
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  featureIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 14,
    background: 'rgba(255,255,255,0.14)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  featureTitle: {
    fontWeight: 800,
    fontSize: 15,
    marginBottom: 4,
  },
  featureText: {
    fontSize: 13.5,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.76)',
  },
  bottomInfoCard: {
    marginTop: 'auto',
    padding: 18,
    borderRadius: 22,
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  bottomInfoTitle: {
    fontSize: 15,
    fontWeight: 800,
    marginBottom: 8,
  },
  bottomInfoText: {
    fontSize: 13.5,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.76)',
  },
  formTop: {
    marginBottom: 18,
  },
  formTitle: {
    fontSize: 30,
    fontWeight: 900,
    color: '#0f172a',
    marginBottom: 6,
  },
  formSub: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 1.6,
  },
  progressCard: {
    padding: 16,
    borderRadius: 18,
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
    border: '1px solid rgba(15,23,42,0.06)',
    marginBottom: 20,
  },
  progressHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  progressTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0f172a',
  },
  progressValue: {
    fontSize: 12.5,
    fontWeight: 800,
    color: '#16a34a',
  },
  progressTrack: {
    width: '100%',
    height: 10,
    borderRadius: 999,
    background: '#e2e8f0',
    overflow: 'hidden',
    marginBottom: 10,
  },
  progressFill: {
    width: '33%',
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #16a34a, #22c55e)',
  },
  progressText: {
    fontSize: 12.8,
    color: '#64748b',
    lineHeight: 1.6,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  twoColMobile: {
    gridTemplateColumns: '1fr',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  label: {
    fontSize: 13,
    fontWeight: 700,
    color: '#334155',
  },
  inputWrap: {
    height: 54,
    borderRadius: 16,
    border: '1px solid rgba(15,23,42,0.10)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 14px',
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14,
    color: '#0f172a',
    minWidth: 0,
  },
  selectWrap: {
    height: 54,
    borderRadius: 16,
    border: '1px solid rgba(15,23,42,0.10)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 14px',
  },
  select: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14,
    color: '#0f172a',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    minWidth: 0,
  },
  summaryCard: {
    padding: 16,
    borderRadius: 18,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
  },
  summaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 800,
    fontSize: 14,
    color: '#0f172a',
    marginBottom: 12,
  },
  summaryBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: 13.5,
    color: '#475569',
    flexWrap: 'wrap',
  },
  agreeWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 13.5,
    color: '#475569',
    lineHeight: 1.7,
  },
  checkbox: {
    marginTop: 2,
    accentColor: '#16a34a',
  },
  submitButton: {
    height: 54,
    borderRadius: 16,
    border: 'none',
    background: 'linear-gradient(135deg, #16a34a, #166534)',
    color: '#fff',
    fontWeight: 800,
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    boxShadow: '0 14px 30px rgba(22,163,74,0.22)',
  },
  demoHint: {
    marginTop: 16,
    padding: 12,
    borderRadius: 14,
    background: '#f8fafc',
    color: '#64748b',
    fontSize: 12.8,
    lineHeight: 1.6,
    border: '1px solid rgba(15,23,42,0.05)',
  },
  footerText: {
    marginTop: 16,
    textAlign: 'center',
    fontSize: 14,
    color: '#64748b',
  },
  footerLink: {
    color: '#16a34a',
    fontWeight: 800,
    textDecoration: 'none',
  },
};

export default Register;